"use client";

import useMediaQuery from "@repo/shared-ui/useMediaQuery";
import {
  useNewsSlider,
  useScrollDetection,
} from "../../components/NewsDetail/hooks";
import { NewsDetailMobile } from "../../components/NewsDetail/components/NewsDetailMobile";
import { NewsDetailDesktop } from "../../components/NewsDetail/components/NewsDetailDesktop";
import { NewsDetailSkeleton } from "../../components/NewsDetail/components/NewsDetailSkeleton";
import Conditional from "@repo/shared-ui/Conditional";
import OpenDematFlowBannerWrapper, { HzBannerAction } from "../../components/Demat/OpenDematFlowBannerWrapper";
import { NewsDetailData } from "./types";
import { NewsAEMContent } from "../../interfaces/NewsAEM.interface";
import NoResultsFound from "../../components/NoResultsFound";

interface NewsDetailClientProps {
  initialNewsData: NewsDetailData;
  initialRelatedArticles?: import("../../interfaces/News.interface").INewsItem[];
  newsId: string;
  newsType?: "trending" | "watchlist" | "portfolio" | "other";
  aemContent?: NewsAEMContent | null;
}

/**
 * Client Component wrapper for News Detail page
 * Handles all interactive features (slider, modal, scroll detection)
 * Receives initial data from server component
 */
export function NewsDetailClient({
  initialNewsData,
  initialRelatedArticles = [],
  newsId,
  newsType = "other",
  aemContent,
}: Readonly<NewsDetailClientProps>) {
  const isMobile = useMediaQuery("(max-width: 1024px)");
  const scrolled = useScrollDetection({ threshold: 100 });

  // Use the slider hook for navigation between news items
  // Pass initial data from SSR so slider can start immediately
  const {
    activeItem: newsData,
    hasPrevious: showLeftButton,
    hasNext: showRightButton,
    goToPrevious: scrollPrev,
    goToNext: scrollNext,
    isLoading,
    error,
  } = useNewsSlider({
    newsId,
    newsType,
    initialData: initialNewsData,
    initialRelatedArticles,
    aemContent,
  });

  const companies = newsData?.companies ?? [];
  const companiesCount = companies.length;

  if (isLoading) {
    return <NewsDetailSkeleton isMobile={isMobile} />;
  }

  if (error || !newsData) {
    return <NoResultsFound aemImages={aemContent?.images} />;
  }

  const originalHzBannerAction = aemContent?.HzBannerAction;

  // Derive an immutable content object — NEVER mutate the AEM original.
  // 1-3 companies → Buy/Sell  |  0 or 4+ companies → AEM defaults (Trade Now)
  const shouldShowBuySell = companiesCount >= 1 && companiesCount <= 3;
  const bannerContent: HzBannerAction | undefined = originalHzBannerAction
    ? {
        ...originalHzBannerAction,
        showTradeNow: shouldShowBuySell ? false : originalHzBannerAction.showTradeNow,
        showBuySellButtons: shouldShowBuySell ? true : originalHzBannerAction.showBuySellButtons,
      }
    : undefined;

  const isExactlyOneCompany = companiesCount === 1;
  const firstCompany = companies?.[0];

  let exchId: string = "";
  let formattedSymbol: string = "";
  let compName: string = "";
  let nseToken: number = 0;
  let bseToken: number = 0;

  if (isExactlyOneCompany && firstCompany) {
    exchId = firstCompany.exchId ?? firstCompany.exchange?.toString() ?? "0";
    formattedSymbol = firstCompany.formattedSymbol ?? firstCompany.symbol ?? "";
    compName = firstCompany.compName ?? firstCompany.name ?? "";
    nseToken = firstCompany.nseToken ?? (firstCompany.exchange === 0 ? (firstCompany.token ?? 0) : 0);
    bseToken = firstCompany.bseToken ?? (firstCompany.exchange === 2 ? (firstCompany.token ?? 0) : 0);
  }

  return (
    <>
      <Conditional if={isMobile}>
        <NewsDetailMobile
          newsData={newsData}
          showLeftButton={showLeftButton}
          showRightButton={showRightButton}
          onScrollPrev={scrollPrev}
          onScrollNext={scrollNext}
        />
      </Conditional>

      <Conditional if={!isMobile}>
        <NewsDetailDesktop
          newsData={newsData}
          scrolled={scrolled}
          showLeftButton={showLeftButton}
          showRightButton={showRightButton}
          onScrollPrev={scrollPrev}
          onScrollNext={scrollNext}
        />
      </Conditional>
      <OpenDematFlowBannerWrapper
        content={bannerContent}
        exchId={exchId}
        formattedSymbol={formattedSymbol}
        compName={compName}
        nseToken={nseToken}
        bseToken={bseToken}
        companies={companies}
      />
    </>
  );
}
